// Phase 10 — <SeoHead/> — per-page <head> injection via react-helmet-async.
//
// Usage (inside any public marketing page):
//
//   import SeoHead from "@/components/SeoHead";
//   import { buildBreadcrumbListSchema } from "@/lib/schema";
//
//   export default function FeaturesPage() {
//       return (
//           <>
//               <SeoHead
//                   path="/features"
//                   schemaJsonLd={[
//                       buildBreadcrumbListSchema({ path: "/features", label: "Features" }),
//                   ]}
//               />
//               {/* page body */}
//           </>
//       );
//   }
//
// Every public page ALSO gets:
//   • <link rel="canonical"> (absolute URL via absoluteUrl).
//   • og:url (same as canonical).
//   • Organization JSON-LD — automatic, always first in the emitted
//     script chain. Callers can append SoftwareApplication / FAQPage /
//     Article / BreadcrumbList via the schemaJsonLd array prop.
//
// The `noIndex` prop renders <meta name="robots" content="noindex"> —
// used by NotFoundPage. Per the brief, the 404 also emits a
// <meta name="prerender-status-code" content="404"> so headless
// renderers can correctly surface the response code.

import { Helmet } from "react-helmet-async";
import { absoluteUrl, getPageMeta } from "@/lib/seoPages";
import { buildOrganizationSchema } from "@/lib/schema";

export default function SeoHead({
    path,
    title,
    description,
    ogImage,
    schemaJsonLd = [],
    noIndex = false,
    prerenderStatusCode = null,
}) {
    const meta = getPageMeta(path) || {};
    const finalTitle = title || meta.title || "UAVSimNet";
    const finalDescription = description || meta.description || "";
    const finalOgImage = ogImage || meta.ogImage || "/og-image.png";
    const canonical = absoluteUrl(path);
    const absoluteOgImage = finalOgImage.startsWith("http")
        ? finalOgImage
        : absoluteUrl(finalOgImage);

    // Organization schema emitted on every page. Callers append
    // specialised schemas (SoftwareApplication, FAQPage, Article,
    // BreadcrumbList) via the schemaJsonLd prop.
    const orgSchema = buildOrganizationSchema();
    const schemas = [orgSchema, ...schemaJsonLd.filter(Boolean)];

    return (
        <Helmet>
            <title>{finalTitle}</title>
            <meta name="description" content={finalDescription} />
            <link rel="canonical" href={canonical} />

            {/* Open Graph */}
            <meta property="og:title" content={finalTitle} />
            <meta property="og:description" content={finalDescription} />
            <meta property="og:url" content={canonical} />
            <meta property="og:type" content="website" />
            <meta property="og:image" content={absoluteOgImage} />
            <meta property="og:site_name" content="UAVSimNet" />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={finalTitle} />
            <meta name="twitter:description" content={finalDescription} />
            <meta name="twitter:image" content={absoluteOgImage} />

            {/* Index control */}
            {noIndex && <meta name="robots" content="noindex,follow" />}
            {prerenderStatusCode && (
                <meta
                    name="prerender-status-code"
                    content={String(prerenderStatusCode)}
                />
            )}

            {/* JSON-LD — one <script> per schema. */}
            {schemas.map((schema, i) => (
                <script
                    key={i}
                    type="application/ld+json"
                    data-testid={`seo-jsonld-${i}`}
                >
                    {JSON.stringify(schema)}
                </script>
            ))}
        </Helmet>
    );
}
